import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { getData, postData } from '../services/request'
import { formToProfilePayload, profileToFormState } from './employmeProfileUtils'

const JobSeekerProfileAiAssist = () => {
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [cvText, setCvText] = useState('')
  const [targetRole, setTargetRole] = useState('')
  const [draft, setDraft] = useState(null)
  const [currentProfile, setCurrentProfile] = useState(null)

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true)
      const url = `${getAPIBaseURL()}/employme/jobseeker/profile`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (!reqError && result?.status === 200) {
        setCurrentProfile(profileToFormState(result.data?.data || {}))
      }
    }
    fetchProfile()
  }, [])

  const generateDraft = async () => {
    setGenerating(true)
    setError('')
    setInfo('')
    setDraft(null)

    const url = `${getAPIBaseURL()}/employme/jobseeker/ai/profile-draft`
    const payload = {
      cvText: cvText.trim(),
      summary: '',
      skillsText: currentProfile?.skills?.join(', ') || '',
      targetRole: targetRole.trim(),
      locationPreference: currentProfile?.locationCountry || ''
    }

    const { error: reqError, result } = await postData(
      url,
      { 'Content-Type': 'application/json' },
      JSON.stringify(payload)
    )
    setGenerating(false)

    if (reqError || !result) {
      setError(result?.data?.data || 'Unable to generate profile draft right now.')
      return
    }
    if (result.status === 200) {
      setDraft(result.data?.data || {})
      setInfo('Draft generated. Review the suggestions below, then apply them to your profile.')
    } else {
      setError(result.data?.data || 'Unable to generate profile draft right now.')
    }
  }

  const applyDraftToProfile = async () => {
    if (!draft) return
    setApplying(true)
    setError('')
    setInfo('')

    const base = currentProfile || profileToFormState({})
    const mergedSkills = Array.isArray(draft.skills) ? draft.skills.filter(Boolean) : base.skills

    const merged = {
      ...base,
      fullName: draft.fullName || base.fullName,
      specialization: draft.headline || base.specialization,
      experienceSummary: draft.summary || base.experienceSummary,
      skills: mergedSkills.length ? mergedSkills : base.skills,
      yearsOfExperience:
        typeof draft.yearsOfExperience === 'number' && !Number.isNaN(draft.yearsOfExperience)
          ? String(draft.yearsOfExperience)
          : base.yearsOfExperience
    }

    const url = `${getAPIBaseURL()}/employme/jobseeker/profile`
    const { error: reqError, result } = await postData(url, {}, formToProfilePayload(merged))
    setApplying(false)

    if (reqError || !result) {
      setError(result?.data?.data || 'Unable to apply draft to your profile.')
      return
    }
    if (result.status === 200 || result.status === 201) {
      setCurrentProfile(merged)
      setInfo('AI suggestions applied to your profile. Review and edit them on your profile page.')
    } else {
      setError(result.data?.data || 'Unable to apply draft to your profile.')
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl animate-fade-in">
      <div className="mb-6">
        <Link
          to="/jobs/profile"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-[#00A5E6]"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to profile
        </Link>
      </div>

      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#00A5E6]">Profile assistant</p>
        <h1 className="mt-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Build your profile faster</h1>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">
          Paste your CV or experience text for suggested profile content. Review everything before applying it to your profile.
        </p>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      {!error && info && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {info}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="h-1 bg-[#00A5E6]" />
        <div className="space-y-6 p-6 sm:p-8">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Target role or industry (optional)
            </label>
            <input
              type="text"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              placeholder="e.g. Customer service, Software development"
              className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm shadow-sm focus:border-[#00A5E6] focus:outline-none focus:ring-2 focus:ring-[#00A5E6]/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Paste your CV or experience
            </label>
            <textarea
              value={cvText}
              onChange={(e) => setCvText(e.target.value)}
              rows={8}
              placeholder="Paste your CV text or a few paragraphs describing your experience, education, and skills..."
              className="w-full resize-y rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm shadow-sm focus:border-[#00A5E6] focus:outline-none focus:ring-2 focus:ring-[#00A5E6]/20"
            />
          </div>

          <button
            type="button"
            disabled={generating || !cvText.trim()}
            onClick={generateDraft}
            className="w-full rounded-lg bg-[#00A5E6] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#008FCC] disabled:opacity-50"
          >
            {generating ? 'Generating suggestions...' : 'Generate profile suggestions'}
          </button>

          <p className="text-[11px] text-slate-400">
            AI can suggest skills, experience, and specialization from your text. Always review for accuracy before
            applying.
          </p>
        </div>
      </div>

      {draft && (
        <div className="mt-8 animate-slide-up overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 bg-slate-50 px-6 py-4">
            <h2 className="text-sm font-bold text-slate-900">Suggested profile content</h2>
            <p className="mt-0.5 text-xs text-slate-500">These fields will be merged into your existing profile.</p>
          </div>
          <div className="space-y-4 p-6 text-sm">
            {draft.fullName ? (
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Full name</span>
                <p className="mt-1 text-slate-800">{draft.fullName}</p>
              </div>
            ) : null}
            {draft.headline ? (
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Specialization</span>
                <p className="mt-1 text-slate-800">{draft.headline}</p>
              </div>
            ) : null}
            {Array.isArray(draft.skills) && draft.skills.length > 0 ? (
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Skills</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {draft.skills.map((skill) => (
                    <span key={skill} className="rounded-md bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {typeof draft.yearsOfExperience === 'number' ? (
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Years of experience</span>
                <p className="mt-1 text-slate-800">{draft.yearsOfExperience}</p>
              </div>
            ) : null}
            {draft.summary ? (
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Experience summary</span>
                <p className="mt-1 whitespace-pre-wrap leading-relaxed text-slate-600">{draft.summary}</p>
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Discard
            </button>
            <button
              type="button"
              disabled={applying}
              onClick={applyDraftToProfile}
              className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-primary-700 disabled:opacity-60"
            >
              {applying ? 'Applying...' : 'Apply to my profile'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default JobSeekerProfileAiAssist
